import { FC, ReactNode, createContext, useEffect, useReducer } from 'react';
import { IUser } from 'src/models/user';
import SuspenseLoader from 'src/components/SuspenseLoader';
import UserService from '../services/UserService';
import { useDispatch } from 'react-redux';

interface IAuthState {
  isInitialised: boolean;
  isAuthenticated: boolean;
  user: IUser | null;
}

export interface IAuthContextValue extends IAuthState {
  method: 'Auth0';
  loginWithPopup: (options?: any) => Promise<void>;
  logout: (options?: any) => Promise<void>;
}

interface IAuthProviderProps {
  children: ReactNode;
}

type InitialiseAction = {
  type: 'INITIALISE';
  payload: {
    isAuthenticated: boolean;
    user: IUser | null;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    user: IUser;
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type RegisterAction = {
  type: 'REGISTER';
};

type Action = InitialiseAction | LoginAction | LogoutAction | RegisterAction;

const initialAuthState: IAuthState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null
};

const reducer = (state: IAuthState, action: Action): IAuthState => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user
      };
    }
    case 'LOGIN': {
      const { user } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext<IAuthContextValue>({
  ...initialAuthState,
  method: 'Auth0',
  loginWithPopup: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

export const AuthProvider: FC<IAuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const dispatcing = useDispatch();
  
  const loginWithPopup = async () => {

    const isAuthenticated = await UserService.isLoggedIn();

    if (isAuthenticated) {
      const user: any =  UserService.getUserInfo();
      await user;
      dispatch({
        type: 'LOGIN',
        payload: {
          user: {
            id: user.sub,
            jobtitle: 'Lead Developer',
            avatar: user.picture,
            email: user.email,
            name: user.name,
            role: user.role,
            location: user.location,
            username: user.preferred_username,
            posts: user.posts,
            coverImg: user.coverImg,
            followers: user.followers,
            description: user.description
          }
        }
      });
    }
  };

  const logout = async () => {

     UserService.doLogout();
    dispatch({
      type: 'LOGOUT'
    });
  };

  useEffect(() => {
    const initialise = async () => {
      try {

        const isAuthenticated = await UserService.isLoggedIn();

        if (isAuthenticated) {
          const username = await UserService.getUsername();
          const user: any = UserService.getUserInfo();
          await user;
          await UserService.refreshOnce(dispatcing);
          await UserService.updateToken(dispatcing);
          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated,
              user: {
                id: user.sub,
                jobtitle: 'Lead Developer',
                avatar: user.picture,
                email: user.email,
                name: username,
                role: user.role,
                location: user.location,
                username: username,
                posts: user.posts,
                coverImg: user.coverImg,
                followers: user.followers,
                description: user.description
              }
            }
          });
        } else {
          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated,
              user: null
            }
          });
        }
      } catch (err) {
        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
        throw new Error(err);
      }
    };

    initialise();
  }, []);

  if (!state.isInitialised) {
    return <SuspenseLoader />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'Auth0',
        loginWithPopup,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
